import {quadraticPolynomialLatex} from "../../../../utils/polynomialLatex";
﻿import React from "react";
import { InlineMath, BlockMath } from "react-katex";

import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";

import FractionInput, {
  formatLatexPreview as formatFracPreview,
} from "../../../../components/inputs/FractionInput";

import {
  parseRootFractionInput,
  formatLatexPreview as formatNumLatex,
} from "../../../../components/inputs/RootFractionInput";

import ParabolaShape from "../../../../components/visuals/ParabolaShape";
import NumberLineRanges from "../../../../components/visuals/NumberLineRanges";
import {
  discriminantSubstitutionLatex,
  substitutedCoefficientLatex,
} from "../../../../utils/quadraticFormulaLatex";

const TOL = 1e-6;
const eq = (a, b) => Math.abs(a - b) <= TOL;

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const SIGN_LATEX = {
  lt: "<",
  le: "\\le",
  gt: ">",
  ge: "\\ge",
};

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

function toMixedFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs + 1e-12);
  const frac = abs - intPart;
  if (frac < 1e-12) return `${sign}${intPart}`;
  // Generated roots have denominator at most 2*A <= 40; do not approximate them.
  for (let d = 1; d <= 40; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 1e-10) {
      const g = gcd(n, d);
      const num = n / g;
      const den = d / g;
      return intPart === 0
        ? `${sign}\\dfrac{${num}}{${den}}`
        : `${sign}${intPart}\\dfrac{${num}}{${den}}`;
    }
  }
  return `${sign}${abs.toFixed(2)}`;
}

function normalizeUserNumberText(txt) {
  return String(txt ?? "")
    .replace(/[−–—]/g, "-")
    .replace(/\u00A0|\u202F|\u2009|\u200A|\u200B/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function parseNumberFromAny(v) {
  if (v == null) return NaN;

  if (typeof v === "object") {
    if (Number.isFinite(v.parsed)) return v.parsed;
    if (typeof v.raw === "string") {
      const s = normalizeUserNumberText(v.raw).replace(",", ".");
      return parseRootFractionInput(s);
    }
  }

  if (typeof v === "number") return v;

  if (typeof v === "string") {
    const s = normalizeUserNumberText(v).replace(",", ".");
    return parseRootFractionInput(s);
  }

  return NaN;
}

function getIntervalRaw(userInput) {
  const val = userInput?.S;
  if (!val) return "";
  if (typeof val === "string") return val;
  if (typeof val === "object" && typeof val.raw === "string") return val.raw;
  return String(val);
}

function getFractionRaw(v) {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object" && typeof v.raw === "string") return v.raw ?? "";
  return "";
}

function linearTermLatex(coeff) {
  if (coeff === 1) return "x";
  if (coeff === -1) return "-x";
  return `${coeff}x`;
}

function leftInnerLatex(a, c) {
  const ax2 = a === 1 ? "x^2" : `${a}x^2`;
  if (c > 0) return `${ax2} + ${c}`;
  return `${ax2} - ${Math.abs(c)}`;
}

function standardQuadraticLatex(A, B, C) {
  return quadraticPolynomialLatex(A,B,C);
}

function generateMay2025OriginalTask() {
  return {
    type: "MaturaScaledQuadraticInequalityOpenTask",
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 10,
    },
    data: {
      k: 3,
      a: 2,
      c: 1,
      b: 11,
      A: 6,
      B: -11,
      C: 3,
      disc: 49,
      sqrtDisc: 7,
      r1: 1 / 3,
      r2: 3 / 2,
      kind: "lt",
      presentation: "parenthesized",
      prettyLatex: "3\\left(2x^2 + 1\\right)\\;<\\;11x",
      standardLatex: "6x^2 - 11x + 3\\;<\\;0",
      maxPoints: 2,
    },
  };
}

export function generateTask(options = {}) {
  if (options.variant === "original" && options.session === "2025_MAJ") {
    return generateMay2025OriginalTask();
  }

  const kind = ["lt", "le", "gt", "ge"][randInt(0, 3)];

  let k, a, c, b, A, B, C, disc, sqrtDisc;

  while (true) {
    k = randInt(2, 5);
    a = randInt(1, 4);
    c = randInt(-10, 10);
    b = randInt(3, 28);

    if (c === 0) continue;

    A = k * a; // > 0
    B = -b;
    C = k * c;

    // Jak w oryginalnym zadaniu CKE: po rozwinięciu cała nierówność nie może
    // mieć wspólnego czynnika, przez który dałoby się ją od razu skrócić.
    if (gcd(gcd(A, B), C) !== 1) continue;

    disc = B * B - 4 * A * C;
    if (disc <= 0) continue;

    sqrtDisc = Math.trunc(Math.sqrt(disc));
    if (sqrtDisc * sqrtDisc !== disc) continue;

    // pełna nierówność kwadratowa
    if (B === 0 || C === 0) continue;

    break;
  }

  const rA = (-B - sqrtDisc) / (2 * A);
  const rB = (-B + sqrtDisc) / (2 * A);
  const r1 = Math.min(rA, rB);
  const r2 = Math.max(rA, rB);

  const presentation = "parenthesized";
  const prettyLatex = `${k}\\left(${leftInnerLatex(a, c)}\\right)\\;${SIGN_LATEX[kind]}\\;${linearTermLatex(
    b
  )}`;

  const standardLatex = `${standardQuadraticLatex(
    A,
    B,
    C
  )}\\;${SIGN_LATEX[kind]}\\;0`;

  return {
    type: "MaturaScaledQuadraticInequalityOpenTask",
    data: {
      k,
      a,
      c,
      b,
      A,
      B,
      C,
      disc,
      sqrtDisc,
      r1,
      r2,
      kind,
      presentation,
      prettyLatex,
      standardLatex,
      maxPoints: 2,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ lineHeight: 1.5 }}>
      <p>Rozwiąż nierówność kwadratową i zapisz zbiór rozwiązań.</p>
      <BlockMath math={data.prettyLatex} />
    </div>
  );
}

export function renderInput(_, userInput = {}, onChange, disabled) {
  const { x1 = "", x2 = "", S = "" } = userInput;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div>
          <b>Krok 1.</b> Oblicz miejsca zerowe trójmianu.
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <InlineMath math={"x_1\\;="} />
          <FractionInput
            label="x₁"
            value={x1}
            onChange={(val) => onChange({ ...userInput, x1: val })}
            disabled={disabled}
            placeholder="np. -3/2"
          />
          <InlineMath math={"x_2\\;="} />
          <FractionInput
            label="x₂"
            value={x2}
            onChange={(val) => onChange({ ...userInput, x2: val })}
            disabled={disabled}
            placeholder="np. 4"
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div>
          <b>Krok 2.</b> Zapisz zbiór rozwiązań nierówności.
        </div>
        <div className="flex flex-col gap-1" style={{ marginBottom: "0.6rem" }}>
          <div style={{ lineHeight: 1 }}>
            <InlineMath math={"x\\;\\in"} />
          </div>
          <IntervalInput
            value={S}
            onChange={(val) => onChange({ ...userInput, S: val })}
            disabled={disabled}
            asString
            previewWithX
            placeholder="np. (-3, 2]"
          />
        </div>
      </div>
    </div>
  );
}

export function renderUserAnswer(userInput = {}) {
  const x1Raw = getFractionRaw(userInput?.x1);
  const x2Raw = getFractionRaw(userInput?.x2);
  const Sraw = getIntervalRaw(userInput);

  const x1Latex =
    x1Raw &&
    (formatFracPreview(x1Raw) ||
      formatNumLatex(String(parseNumberFromAny(userInput?.x1)), ""));
  const x2Latex =
    x2Raw &&
    (formatFracPreview(x2Raw) ||
      formatNumLatex(String(parseNumberFromAny(userInput?.x2)), ""));
  const SLat = Sraw.trim() ? intervalToLatex(Sraw, { withX: true }) : "";

  return (
    <div className="flex flex-col gap-1">
      <div>
        {x1Latex || x2Latex ? (
          <InlineMath
            math={`x_1 = ${x1Latex || "\\;?"},\\; x_2 = ${x2Latex || "\\;?"}`}
          />
        ) : (
          <i>(brak odpowiedzi dla miejsc zerowych)</i>
        )}
      </div>
      <div>{SLat ? <InlineMath math={SLat} /> : <i>(brak odpowiedzi dla przedziału)</i>}</div>
    </div>
  );
}

export function renderAnswer({ data }) {
  const x1L = toMixedFractionLatex(data.r1);
  const x2L = toMixedFractionLatex(data.r2);
  const inclusive = data.kind === "le" || data.kind === "ge";
  const lBrace = inclusive ? "[" : "(";
  const rBrace = inclusive ? "]" : ")";

  const solutionInterval =
    data.kind === "lt" || data.kind === "le"
      ? `${lBrace}${x1L},\\;${x2L}${rBrace}`
      : `(-\\infty,\\;${x1L}${rBrace}\\cup ${lBrace}${x2L},\\;+\\infty)`;

  return (
    <div
      style={{
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        gap: "0.25rem",
        lineHeight: 1.4,
      }}
    >
      <div>
        <InlineMath math={`x_1=${x1L},\\;x_2=${x2L}`} />
      </div>
      <div>
        <InlineMath math={`x\\in ${solutionInterval}`} />
      </div>
    </div>
  );
}

function expectedSolution({ r1, r2, kind }) {
  const inclusive = kind === "le" || kind === "ge";
  if (kind === "lt" || kind === "le") {
    return {
      kind: "between",
      seg: { L: r1, R: r2, Lclosed: inclusive, Rclosed: inclusive },
      inclusive,
    };
  }
  return {
    kind: "outside",
    left: { L: -Infinity, R: r1, Rclosed: inclusive },
    right: { L: r2, R: Infinity, Lclosed: inclusive },
    inclusive,
  };
}

function checkRoots({ r1, r2 }, userInput) {
  const u1 = parseNumberFromAny(userInput?.x1);
  const u2 = parseNumberFromAny(userInput?.x2);
  if (!Number.isFinite(u1) || !Number.isFinite(u2)) return false;
  const [a1, a2] = [r1, r2].sort((x, y) => x - y);
  const [b1, b2] = [u1, u2].sort((x, y) => x - y);
  return eq(a1, b1) && eq(a2, b2);
}

function intervalsEqual(ans, parsed) {
  if (!parsed || parsed.kind !== "intervals") return false;
  const norm = (I) => ({
    L: I.left,
    R: I.right,
    Lc: !!I.leftClosed,
    Rc: !!I.rightClosed,
  });
  const expected = [];
  if (ans.kind === "between") {
    expected.push({
      L: ans.seg.L,
      R: ans.seg.R,
      Lc: ans.seg.Lclosed,
      Rc: ans.seg.Rclosed,
    });
  } else {
    expected.push({ L: -Infinity, R: ans.left.R, Lc: false, Rc: ans.left.Rclosed });
    expected.push({ L: ans.right.L, R: Infinity, Lc: ans.right.Lclosed, Rc: false });
  }

  if (expected.length !== parsed.intervals.length) return false;
  const got = parsed.intervals.map(norm);
  const used = new Array(got.length).fill(false);

  for (const E of expected) {
    let ok = false;
    for (let i = 0; i < got.length; i++) {
      if (used[i]) continue;
      const G = got[i];
      const lOk = E.L === -Infinity ? G.L === -Infinity : eq(E.L, G.L);
      const rOk = E.R === Infinity ? G.R === Infinity : eq(E.R, G.R);
      if (lOk && rOk && E.Lc === G.Lc && E.Rc === G.Rc) {
        used[i] = true;
        ok = true;
        break;
      }
    }
    if (!ok) return false;
  }

  return true;
}

function checkInterval(data, userInput) {
  const raw = getIntervalRaw(userInput);
  if (!raw.trim()) return false;
  const parsed = parseIntervalInput(raw);
  return intervalsEqual(expectedSolution(data), parsed);
}

export function checkAnswer({ data }, userInput = {}) {
  let goodRoots = checkRoots(data, userInput);
  const goodInterval = checkInterval(data, userInput);

  const x1Missing = !getFractionRaw(userInput?.x1).trim();
  const x2Missing = !getFractionRaw(userInput?.x2).trim();
  if (x1Missing && x2Missing && goodInterval) goodRoots = true;

  const score = goodInterval ? 2 : goodRoots ? 1 : 0;
  const rawS = getIntervalRaw(userInput);
  const parsedS = rawS.trim() ? parseIntervalInput(rawS) : null;
  const invalidOrder = !!parsedS && parsedS.kind === "invalid_order";
  const message = invalidOrder
    ? "Uwaga: przedział zapisano od większej do mniejszej liczby. Zapisuj przedziały od mniejszej do większej (lewy koniec ≤ prawy koniec)."
    : null;

  return {
    isCorrect: score === data.maxPoints,
    score,
    maxScore: data.maxPoints,
    parts: {
      "miejsca zerowe trójmianu": goodRoots,
      "zbiór rozwiązań nierówności": goodInterval,
    },
    message,
    messageType: message ? "hint" : null,
  };
}

export function renderExplanation({ data }) {
  const { A, B, C, disc, sqrtDisc, r1, r2, kind, prettyLatex, standardLatex } =
    data;

  const inclusive = kind === "le" || kind === "ge";
  const x1L = toMixedFractionLatex(r1);
  const x2L = toMixedFractionLatex(r2);

  const insideLatex =
    kind === "lt" || kind === "le"
      ? `${inclusive ? "[" : "("}${x1L},\\;${x2L}${inclusive ? "]" : ")"}`
      : `(-\\infty,\\;${x1L}${inclusive ? "]" : ")"}\\cup${
          inclusive ? "[" : "("
        }${x2L},\\;+\\infty)`;

  const minusBLatex = B < 0 ? `-(${B})` : `-${B}`;
  const deltaSubstitution = discriminantSubstitutionLatex(A, B, C, disc);
  const aLatex = substitutedCoefficientLatex(A);
  const dnm = 2 * A;
  const n1 = -B - sqrtDisc;
  const n2 = -B + sqrtDisc;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Przenosimy wszystko na jedną stronę i
        porządkujemy:
      </p>
      <BlockMath math={prettyLatex} />
      <BlockMath math={`\\Longleftrightarrow\\quad ${standardLatex}`} />

      <p>
        <strong>Krok 2.</strong> Liczymy deltę i miejsca zerowe:
      </p>
      <BlockMath math={`\\Delta=b^2-4ac=${deltaSubstitution}`} />
      <BlockMath math={`\\sqrt{\\Delta}=${sqrtDisc}`} />
      <BlockMath
        math={`x_1=\\dfrac{${minusBLatex}-${sqrtDisc}}{2\\cdot ${aLatex}}=\\dfrac{${n1}}{${dnm}}=${x1L}`}
      />
      <BlockMath
        math={`x_2=\\dfrac{${minusBLatex}+${sqrtDisc}}{2\\cdot ${aLatex}}=\\dfrac{${n2}}{${dnm}}=${x2L}`}
      />

      <p>
        <strong>Krok 3.</strong> Z wykresu paraboli i znaku nierówności:
      </p>
      <div style={{ maxWidth: 520, margin: "0.5rem auto" }}>
        <ParabolaShape
          aSign={1}
          xRange={6}
          yWindow={6}
          showXAxis
          showZeroTicks
          curveColor="#f59e0b"
          labelLeftTex={x1L}
          labelRightTex={x2L}
          highlightMode={kind === "lt" || kind === "le" ? "between" : "outside"}
          highlightLeftClosed={inclusive}
          highlightRightClosed={inclusive}
          highlightColor="#a78bfa"
        />
      </div>

      <BlockMath math={`x\\in ${insideLatex}`} />

      <div style={{ maxWidth: 520, margin: "0.3rem auto" }}>
        <NumberLineRanges
          lockPair
          showZero
          ranges={
            kind === "lt" || kind === "le"
              ? [
                  {
                    from: r1,
                    to: r2,
                    leftClosed: inclusive,
                    rightClosed: inclusive,
                    leftLabelLatex: x1L,
                    rightLabelLatex: x2L,
                  },
                ]
              : [
                  {
                    from: -Infinity,
                    to: r1,
                    leftClosed: false,
                    rightClosed: inclusive,
                    rightLabelLatex: x1L,
                  },
                  {
                    from: r2,
                    to: Infinity,
                    leftClosed: inclusive,
                    rightClosed: false,
                    leftLabelLatex: x2L,
                  },
                ]
          }
        />
      </div>

      <p>
        Odpowiedź: <InlineMath math={`x\\in ${insideLatex}`} />.
      </p>
    </div>
  );
}
